import type { AppStore } from '../stores/createStore';
import { refreshAccessToken, logout } from '../stores/slices/authSlice';

/**
 * tRPC 代理客户端类型（用于直接调用 mutation，不使用 React hooks）
 */
interface TRPCProxyClient {
  auth: {
    refresh: {
      mutate: (input: { refreshToken: string }) => Promise<{
        accessToken: string;
        refreshToken?: string;
      }>;
    };
  };
}

/**
 * 刷新 Access Token
 * 使用 Refresh Token 获取新的 Access Token
 * 
 * ✅ 统一使用 tRPC 客户端，不再使用原生 fetch
 * 
 * @param store Redux store 实例
 * @param trpcClient tRPC 客户端实例
 * @returns 是否刷新成功
 */
export async function refreshToken(
  store: AppStore,
  trpcProxyClient: TRPCProxyClient
): Promise<boolean> {
  try {
    const state = store.getState();
    const refreshTokenValue = state.auth.refreshToken;

    if (!refreshTokenValue) {
      // 没有 refresh token，需要重新登录
      store.dispatch(logout());
      return false;
    }

    // ✅ 使用 tRPC 代理客户端直接调用刷新接口（不使用 React hooks）
    const result = await trpcProxyClient.auth.refresh.mutate({
      refreshToken: refreshTokenValue,
    });

    if (result?.accessToken) {
      // 更新 token
      store.dispatch(
        refreshAccessToken({
          token: result.accessToken,
          refreshToken: result.refreshToken,
        })
      );
      return true;
    }

    return false;
  } catch (error) {
    console.error('刷新 token 失败:', error);
    store.dispatch(logout());
    return false;
  }
}

/**
 * 检查 token 是否即将过期
 * 如果 token 在 5 分钟内过期，自动刷新
 * 
 * @param store Redux store 实例
 * @param trpcClient tRPC 客户端实例
 * @returns Promise<boolean> token 是否有效
 */
export function checkAndRefreshToken(
  store: AppStore,
  trpcProxyClient: TRPCProxyClient
): Promise<boolean> {
  return new Promise((resolve) => {
    const state = store.getState();
    const token = state.auth.token;

    if (!token) {
      resolve(false);
      return;
    }

    try {
      // 解析 JWT token（不验证签名，只读取过期时间）
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // 转换为毫秒
      const now = Date.now();
      const timeUntilExpiry = exp - now;

      // 如果 token 在 5 分钟内过期，或者已经过期，则刷新
      if (timeUntilExpiry < 5 * 60 * 1000) {
        refreshToken(store, trpcProxyClient).then(resolve);
      } else {
        resolve(true);
      }
    } catch (error) {
      // 解析失败，尝试刷新
      refreshToken(store, trpcProxyClient).then(resolve);
    }
  });
}

